import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { fmtDatum, berekenUrenMetPauze, berekenVerlofUrenHeleDag } from '../utils/datum';
import sharepointConfig from '../config/sharepointConfig';

function Ziekmelding() {
  const navigate = useNavigate();
  const { currentUser, isAdmin, isManager, canManage, medewerkers, feestdagen, indienVerlofaanvraag } = useApp();

  const [form, setForm] = useState({
    StartDatum: new Date().toISOString().split('T')[0],
    EindDatum: '', isDagdeel: false, StartTijd: '', EindTijd: '',
    Uren: '', Reden: '', isOpenZiek: true,
  });
  const [isVoorAnder, setIsVoorAnder] = useState(false);
  const [targetMwId, setTargetMwId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const targetUser = isVoorAnder ? medewerkers.find(m => m.Id === targetMwId) : currentUser;
  const beschikbareMedewerkers = medewerkers.filter(m => (m.Status || 'actief') === 'actief' && m.Id !== currentUser?.Id);

  const handleChange = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // Uren berekening voor ziek met einddatum (werkrooster-aware)
  const berekendUren = (() => {
    if (form.isOpenZiek || !form.StartDatum || !form.EindDatum) return 0;
    if (form.isDagdeel && form.StartTijd && form.EindTijd) {
      const info = berekenUrenMetPauze(form.StartTijd, form.EindTijd);
      return info.netto;
    }
    const werkrooster = targetUser?.Werkrooster || null;
    const feestLijst = (feestdagen || []).map(f => f.Datum);
    const hd = berekenVerlofUrenHeleDag(form.StartDatum, form.EindDatum, feestLijst, werkrooster);
    return hd.uren;
  })();

  const uren = parseFloat(form.Uren) || berekendUren;

  const handleSubmit = async () => {
    if (!form.StartDatum) { setError('Kies een startdatum.'); return; }
    if (!form.isOpenZiek && !form.EindDatum) { setError('Kies een einddatum of kies open ziekmelding.'); return; }
    if (isVoorAnder && !targetMwId) { setError('Selecteer een medewerker.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const mwId = isVoorAnder ? targetMwId : currentUser.Id;
      const mwNaam = isVoorAnder ? targetUser.Naam : currentUser.Naam;
      await indienVerlofaanvraag({
        medewerkerId: mwId,
        medewerkerNaam: mwNaam,
        ingediendDoor: currentUser.Id,
        ingediendDoorNaam: currentUser.Naam,
        Type: 'ziek',
        StartDatum: form.StartDatum,
        EindDatum: form.isOpenZiek ? '' : form.EindDatum,
        StartTijd: form.isDagdeel ? form.StartTijd : null,
        EindTijd: !form.isOpenZiek && form.isDagdeel ? form.EindTijd : '',
        Uren: form.isOpenZiek ? 0 : uren,
        Reden: form.Reden,
        isOpenZiek: form.isOpenZiek,
      });

      setSubmitted(true);
    } catch (err) { console.error(err); setError('Fout bij indienen.'); }
    finally { setSubmitting(false); }
  };

  if (!currentUser) return null;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div style={styles.card} className="r-card">
        {submitted ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🏥</div>
            <h2 style={styles.successTitle}>Ziekmelding geregistreerd!</h2>
            <p style={styles.successText}>
              Ziekmelding{isVoorAnder ? ` voor ${targetUser?.Naam}` : ''} vanaf {fmtDatum(form.StartDatum)}{form.StartTijd ? ` ${form.StartTijd}` : ''}
              {!form.isOpenZiek && form.EindDatum ? ` t/m ${fmtDatum(form.EindDatum)}` : ''} is geregistreerd.
              {form.isOpenZiek ? ' Betermelden kan via het Verlofoverzicht.' : ''}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20 }}>
              <button style={styles.secondaryBtn} onClick={() => navigate('/verlof-overzicht')}>Naar overzicht</button>
              <button style={styles.primaryBtn} onClick={() => { setSubmitted(false); setForm({ StartDatum: new Date().toISOString().split('T')[0], EindDatum: '', isDagdeel: false, StartTijd: '', EindTijd: '', Uren: '', Reden: '', isOpenZiek: true }); setTargetMwId(''); }}>Nieuwe ziekmelding</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <span style={{ fontSize: 28 }}>🏥</span>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>Ziekmelding</h2>
                <p style={{ fontSize: 13, color: '#605e5c', margin: 0 }}>Meld jezelf of een medewerker ziek.</p>
              </div>
            </div>

            {/* Voor wie */}
            {canManage && (
              <div style={styles.field}>
                <div style={styles.toggleRow}>
                  <button style={{ ...styles.toggleBtn, ...(!isVoorAnder ? styles.toggleBtnActive : {}) }}
                    onClick={() => { setIsVoorAnder(false); setTargetMwId(''); }}>
                    Mezelf ziekmelden
                  </button>
                  <button style={{ ...styles.toggleBtn, ...(isVoorAnder ? styles.toggleBtnActive : {}) }}
                    onClick={() => setIsVoorAnder(true)}>
                    Medewerker ziekmelden
                  </button>
                </div>
                {isVoorAnder && (
                  <select style={{ ...styles.input, marginTop: 12 }} value={targetMwId}
                    onChange={e => setTargetMwId(e.target.value)}>
                    <option value="" disabled>--- Kies ---</option>
                    {beschikbareMedewerkers.map(m => (
                      <option key={m.Id} value={m.Id}>{m.Naam} ({m.Afdeling || 'Medewerker'})</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Type ziekmelding */}
            <div style={styles.toggleRow}>
              <button style={{ ...styles.toggleBtn, ...(form.isOpenZiek ? styles.toggleBtnActive : {}) }}
                onClick={() => { handleChange('isOpenZiek', true); handleChange('EindDatum', ''); handleChange('EindTijd', ''); handleChange('Uren', ''); }}>
                🏥 Ziekmelden (betermelden later)
              </button>
              <button style={{ ...styles.toggleBtn, ...(!form.isOpenZiek ? styles.toggleBtnActive : {}) }}
                onClick={() => handleChange('isOpenZiek', false)}>
                🤒 Ziek met einddatum
              </button>
            </div>

            {form.isOpenZiek ? (
              <>
                <div style={{ ...styles.previewBox, background: '#fff4ce', borderColor: '#FFB900', color: '#7A6400', marginTop: 16 }}>
                  🏥 <strong>Open ziekmelding</strong> — {isVoorAnder ? `${targetUser?.Naam || 'Medewerker'} wordt` : 'Je wordt'} ziek gemeld vanaf de startdatum. Betermelden doe je later via het Verlofoverzicht.
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Startdatum ziekte</label>
                  <input type="date" style={styles.input} value={form.StartDatum}
                    onChange={e => handleChange('StartDatum', e.target.value)}
                    max={new Date().toISOString().split('T')[0]} />
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#605e5c', cursor: 'pointer', marginBottom: 16 }}>
                  <input type="checkbox" checked={form.isDagdeel}
                    onChange={e => handleChange('isDagdeel', e.target.checked)} />
                  Vanaf een specifiek tijdstip
                </label>
                {form.isDagdeel && (
                  <div style={styles.field}>
                    <label style={styles.label}>Ziek vanaf</label>
                    <input type="time" style={styles.input} value={form.StartTijd}
                      onChange={e => handleChange('StartTijd', e.target.value)} />
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }} className="r-form-grid">
                  <div style={styles.field}>
                    <label style={styles.label}>Startdatum</label>
                    <input type="date" style={styles.input} value={form.StartDatum}
                      onChange={e => handleChange('StartDatum', e.target.value)} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Einddatum</label>
                    <input type="date" style={styles.input} value={form.EindDatum} min={form.StartDatum}
                      onChange={e => handleChange('EindDatum', e.target.value)} />
                  </div>
                </div>
                {berekendUren > 0 && (
                  <div style={styles.field}>
                    <label style={styles.label}>Uren berekening</label>
                    <input type="number" style={styles.input} value={form.Uren || berekendUren}
                      onChange={e => handleChange('Uren', e.target.value)} min="0" step="0.5" />
                  </div>
                )}
              </>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Opmerkingen (optioneel)</label>
              <textarea style={styles.textarea} value={form.Reden}
                onChange={e => handleChange('Reden', e.target.value)} placeholder="Bijv. griep, migraine..." rows={2} />
            </div>

            {error && <div style={styles.errorBox}>⚠️ {error}</div>}

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button style={styles.secondaryBtn} onClick={() => navigate('/')}>Annuleren</button>
              <button style={{ ...styles.primaryBtn, opacity: submitting ? 0.6 : 1 }}
                onClick={handleSubmit} disabled={submitting || !form.StartDatum}>
                {submitting ? 'Bezig...' : '🏥 Ziekmelden'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: { background: '#fff', border: '1px solid #edebe9', borderRadius: 16, padding: '24px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#323130', marginBottom: 6 },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #d2d0ce', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #d2d0ce', borderRadius: 10, fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
  toggleRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  toggleBtn: { flex: 1, minWidth: 120, padding: '10px 16px', border: '2px solid #edebe9', borderRadius: 10, background: '#faf9f8', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#605e5c', textAlign: 'center', transition: 'all 0.15s' },
  toggleBtnActive: { borderColor: '#E86E1C', background: '#FFF4EC', color: '#E86E1C' },
  previewBox: { padding: '14px 18px', borderRadius: 10, fontSize: 13, marginBottom: 16, border: '1px solid', lineHeight: 1.6 },
  errorBox: { background: '#FDE7E9', color: '#D13438', padding: '10px 16px', borderRadius: 10, fontSize: 13, marginBottom: 12 },
  primaryBtn: { flex: 1, padding: '12px 24px', background: '#D83B01', color: '#fff', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  secondaryBtn: { padding: '12px 24px', background: '#f3f2f1', color: '#323130', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  successTitle: { fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 },
  successText: { fontSize: 14, color: '#605e5c', lineHeight: 1.6 },
};

export default Ziekmelding;
